/**
 * PWAEducationScreen
 * Explains the value of installing as PWA BEFORE prompting install
 * This is shown during onboarding on all platforms (mobile and desktop)
 */

import React, { useState } from 'react';
import styles from '../../styles/OnboardingScreen.module.css';
import { usePWAInstall } from '../../hooks/usePWAInstall';

function PWAEducationScreen({ onNext, onBack }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const { promptInstall, isInstallable, isIOS } = usePWAInstall();

  const handleNext = async () => {
    if (!isSubmitting) {
      setIsSubmitting(true);

      // Check platform and handle accordingly
      if (isIOS && isInstallable) {
        // iOS - show manual instructions
        setShowIOSInstructions(true);
        setIsSubmitting(false);
        return;
      }

      // Android/Desktop - try to show native prompt if available
      if (isInstallable) {
        await promptInstall();
      }

      // Continue to next screen
      await onNext();
      setIsSubmitting(false);
    }
  };

  const handleIOSContinue = async () => {
    setShowIOSInstructions(false);
    await onNext();
  };

  // Detect platform for appropriate messaging
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Construct image URL based on environment
  const getImageUrl = (filename) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (apiUrl) {
      return `${apiUrl}/onboarding/${filename}`;
    }
    return `/onboarding/${filename}`;
  };

  return (
    <>
      {showIOSInstructions && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <div className={styles.modalTitle}>Install Sophia on iOS</div>
            <div className={styles.iosInstructions}>
              <div className={styles.iosStep}>
                <div className={styles.iosStepNumber}>1</div>
                <div className={styles.iosStepText}>
                  Tap the <strong>Share</strong> button <span className={styles.iosIcon}>⎘</span> at the bottom of Safari
                </div>
              </div>
              <div className={styles.iosStep}>
                <div className={styles.iosStepNumber}>2</div>
                <div className={styles.iosStepText}>
                  Scroll down and tap <strong>"Add to Home Screen"</strong> <span className={styles.iosIcon}>➕</span>
                </div>
              </div>
              <div className={styles.iosStep}>
                <div className={styles.iosStepNumber}>3</div>
                <div className={styles.iosStepText}>
                  Tap <strong>"Add"</strong> in the top right corner
                </div>
              </div>
            </div>
            <div className={styles.modalButtons}>
              <button
                className={styles.modalBtnYes}
                onClick={handleIOSContinue}
              >
                Got It
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.introContent}>
        {/* Hero image header */}
        <div className={styles.welcomeHeader}>
          <img
            src={getImageUrl('hero-install-app.png')}
            alt="Install Sophia"
            className={styles.welcomeHeroImage}
          />
        </div>

      <div className={styles.topSection}>
      </div>

      <div className={styles.introMiddleSection}>
        <div>
          <h1 className={styles.title}>Install Sophia</h1>

          <p className={styles.description}>
            Get the best experience by installing Sophia like a native app.
          </p>

          <div className={styles.benefitsList}>
            <div className={styles.benefit}>
              <span className={styles.benefitIcon}>⚡</span>
              <span>{isMobile ? 'Quick access from your home screen' : 'Launch from your desktop or taskbar'}</span>
            </div>
            <div className={styles.benefit}>
              <span className={styles.benefitIcon}>📴</span>
              <span>Works offline when you need it</span>
            </div>
            <div className={styles.benefit}>
              <span className={styles.benefitIcon}>🎯</span>
              <span>Feels like a real app, not a website</span>
            </div>
          </div>

          <div className={styles.appIconPreview}>
            <div className={styles.mockHomeScreen}>
              <div className={styles.mockAppIcon}>
                <img
                  src="/icon-512x512.png"
                  alt="Sophia App Icon"
                  className={styles.mockAppIconImage}
                />
              </div>
              <div className={styles.mockAppLabel}>Sophia</div>
            </div>
          </div>

          <p className={styles.note}>
            This is completely optional - the app works great in your browser too!
          </p>
        </div>
      </div>

      <div className={styles.bottomSection}>
        {/* Show Install button - visible for all non-iOS platforms */}
        {!isIOS && (
          <button
            className={`${styles.button} ${styles.buttonPrimary} ${styles.installButton}`}
            onClick={async () => {
              if (isInstallable) {
                await promptInstall();
              }
              await onNext();
            }}
            disabled={isSubmitting}
          >
            {!isInstallable ? 'Continue (App Installed ✓)' : 'Install Now'}
          </button>
        )}

        <div className={styles.buttonGroup}>
          {onBack && (
            <button
              className={`${styles.button} ${styles.buttonSecondary}`}
              onClick={onBack}
              disabled={isSubmitting}
            >
              Back
            </button>
          )}
          <button
            className={`${styles.button} ${isInstallable && !isIOS ? styles.buttonSecondary : styles.buttonPrimary}`}
            onClick={handleNext}
            disabled={isSubmitting}
          >
            {isInstallable && !isIOS ? 'Skip' : 'Sounds Good'}
          </button>
        </div>
      </div>
      </div>
    </>
  );
}

export default PWAEducationScreen;
